import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { WorkOrderStatusChip } from "@/components/search-work-orders/WorkOrderTransferTable";
import { formatDate } from "@/lib/format";
import type { WorkOrder } from "@/mocks/search-work-orders";

interface RowDetailDrawerProps {
  workOrder: WorkOrder | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b py-1.5 text-sm last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

export function RowDetailDrawer({ workOrder, open, onOpenChange }: RowDetailDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/35 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col gap-4 border-l bg-card p-6 shadow-lg outline-none data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right">
          {workOrder && (
            <>
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {workOrder.workOrderNo}
                </p>
                <DialogPrimitive.Title className="font-display text-lg font-semibold">
                  {workOrder.itemDescription}
                </DialogPrimitive.Title>
                <DialogPrimitive.Description className="sr-only">
                  Work order detail for {workOrder.workOrderNo}
                </DialogPrimitive.Description>
                <div className="mt-2">
                  <WorkOrderStatusChip status={workOrder.status} />
                </div>
              </div>
              <dl className="flex-1 space-y-0 overflow-y-auto">
                <DetailRow label="Internal ID" value={workOrder.internalId} />
                <DetailRow label="Item" value={workOrder.itemCode} />
                <DetailRow label="Location" value={workOrder.locationName} />
                <DetailRow label="Quantity" value={workOrder.quantity} />
                <DetailRow label="Start date" value={formatDate(workOrder.startDate)} />
                <DetailRow label="End date" value={formatDate(workOrder.endDate)} />
                <DetailRow label="Firmed" value={workOrder.firmed ? "Yes" : "No"} />
                <DetailRow label="Memo" value={workOrder.memo || "—"} />
              </dl>
            </>
          )}
          <DialogPrimitive.Close className="absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
